import { supabase } from '../auth/supabase'
import type { ID, Player, StatEvent, StatTypeId } from '../domain/types'

/**
 * Talking to the live-scoring tables.
 *
 * Every write is an RPC, never a table write: `stat_events`, `game_claims` and
 * `game_scoring_state` grant SELECT and nothing else, so the rules about who
 * holds the pen live in one place in the database rather than being reimplemented
 * (and eventually disagreed with) here.
 *
 * Nothing in this file throws for an ordinary failure. Being offline is the
 * normal state of a phone in a stadium, and the callers all have somewhere
 * better to put the message than an unhandled rejection.
 */

export interface ServerEventRow {
  client_event_id: string
  game_id: string
  player_id: string | null
  fill_in_label: string | null
  client_player_id: string | null
  stat_type: string
  period: number
  occurred_at: string
  deleted: boolean
  client_updated_at: string | null
  recorded_by: string | null
}

export interface LiveClaim {
  claim_id: string
  game_id: string
  claimed_by: string
  claimed_at: string
  holder_player_id: string | null
  holder_name: string | null
  last_event_at: string | null
  last_activity_at: string
  is_stale: boolean
  event_count: number
}

export interface ScoringStateRow {
  game_id: string
  roster: ScoringRosterEntry[]
  current_period: number
  period_format: 'halves' | 'quarters'
  enabled_stat_types: string[]
  home_away: string | null
  opponent_name: string | null
  updated_at: string
}

/** A grid row as the scorer sees it, so every device draws the same one. */
export interface ScoringRosterEntry {
  id: string
  name: string
  isFillIn?: boolean
}

export interface ClaimOutcome {
  held: boolean
  mine: boolean
  claim_id?: string
  claimed_at?: string
  holder_name?: string | null
  holder_id?: string | null
  last_activity_at?: string
  is_stale?: boolean
  took_over?: boolean
}

export interface PushOutcome {
  written: number
  rejected: { game_id: string; reason: string }[]
}

/** Everything here returns this shape; `error` is a sentence, not an exception. */
export type Result<T> = { ok: true; data: T } | { ok: false; error: string }

function offline(): Result<never> {
  return { ok: false, error: 'No connection to the server' }
}

function describe(err: unknown): string {
  if (err && typeof err === 'object' && 'message' in err) return String((err as { message: unknown }).message)
  return String(err)
}

// ---------------------------------------------------------------------------
// Mapping between the local event shape and the server's.
// ---------------------------------------------------------------------------

/**
 * A fill-in has no players row on the server (02.4(3)), so their taps travel as
 * a label. Without this they would fail the foreign key and never leave the
 * iPad -- silently, which is the failure mode this project keeps paying for.
 */
export function toServerEvent(e: StatEvent, players: Player[]): Record<string, unknown> {
  const player = players.find((p) => p.id === e.playerId)
  const isFillIn = player?.isFillIn === true || !player?.playhqProfileId
  return {
    client_event_id: e.id,
    game_id: e.gameId,
    player_id: isFillIn ? null : e.playerId,
    fill_in_label: isFillIn ? (player?.name ?? 'Fill-in') : null,
    client_player_id: isFillIn ? e.playerId : null,
    client_game_id: e.gameId,
    stat_type: e.statType,
    period: e.period,
    occurred_at: new Date(e.at).toISOString(),
    deleted: e.deleted,
    updated_at: new Date(e.updatedAt).toISOString(),
    playhq_game_id: e.playhqGameId ?? null,
    playhq_profile_id: e.playhqProfileId ?? null,
  }
}

/**
 * Server row to the local shape. `playerId` resolves to the server's player id
 * for a squad member -- the two are the same id since 03.1(2) -- and to the
 * originating device's local id for a fill-in, which is the only handle that
 * keeps one fill-in's taps together.
 */
export function fromServerEvent(row: ServerEventRow): StatEvent {
  const at = Date.parse(row.occurred_at)
  const updated = row.client_updated_at ? Date.parse(row.client_updated_at) : at
  return {
    id: row.client_event_id,
    gameId: row.game_id,
    playerId: (row.player_id ?? row.client_player_id ?? 'unknown') as ID,
    statType: row.stat_type as StatTypeId,
    period: row.period,
    at,
    deleted: row.deleted,
    createdAt: at,
    updatedAt: updated,
    syncedAt: updated,
  }
}

const EVENT_COLUMNS =
  'client_event_id, game_id, player_id, fill_in_label, client_player_id, stat_type, period, occurred_at, deleted, client_updated_at, recorded_by'

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

export async function fetchLiveClaims(): Promise<Result<LiveClaim[]>> {
  if (!supabase) return offline()
  const { data, error } = await supabase.from('live_claims').select('*')
  if (error) return { ok: false, error: describe(error) }
  return { ok: true, data: (data ?? []) as LiveClaim[] }
}

export async function fetchGameEvents(gameId: string): Promise<Result<ServerEventRow[]>> {
  if (!supabase) return offline()
  const { data, error } = await supabase.from('stat_events').select(EVENT_COLUMNS).eq('game_id', gameId)
  if (error) return { ok: false, error: describe(error) }
  return { ok: true, data: (data ?? []) as ServerEventRow[] }
}

export async function fetchAllEvents(): Promise<Result<ServerEventRow[]>> {
  if (!supabase) return offline()
  // Anon may read these only on staging (RLS gates it); production never uses
  // this path.
  const { data, error } = await supabase.from('stat_events').select(EVENT_COLUMNS)
  if (error) return { ok: false, error: describe(error) }
  return { ok: true, data: (data ?? []) as ServerEventRow[] }
}

export async function fetchScoringState(gameId: string): Promise<Result<ScoringStateRow | null>> {
  if (!supabase) return offline()
  const { data, error } = await supabase.from('game_scoring_state').select('*').eq('game_id', gameId).limit(1)
  if (error) return { ok: false, error: describe(error) }
  return { ok: true, data: ((data ?? [])[0] as ScoringStateRow | undefined) ?? null }
}

// ---------------------------------------------------------------------------
// Writes -- all through SECURITY DEFINER functions.
// ---------------------------------------------------------------------------

export async function claimGame(gameId: string, takeover = false, deviceLabel?: string): Promise<Result<ClaimOutcome>> {
  if (!supabase) return offline()
  const { data, error } = await supabase.rpc('claim_game', {
    p_game_id: gameId,
    p_takeover: takeover,
    p_device_label: deviceLabel ?? null,
  })
  if (error) return { ok: false, error: describe(error) }
  return { ok: true, data: data as ClaimOutcome }
}

export async function releaseClaim(gameId: string, reason: 'ended' | 'manual' | 'takeover' = 'manual'): Promise<Result<boolean>> {
  if (!supabase) return offline()
  const { data, error } = await supabase.rpc('release_claim', { p_game_id: gameId, p_reason: reason })
  if (error) return { ok: false, error: describe(error) }
  return { ok: true, data: (data as { released: boolean }).released }
}

export async function pushEvents(events: StatEvent[], players: Player[]): Promise<Result<PushOutcome>> {
  if (!supabase) return offline()
  if (events.length === 0) return { ok: true, data: { written: 0, rejected: [] } }
  const { data, error } = await supabase.rpc('push_stat_events', {
    p_events: events.map((e) => toServerEvent(e, players)),
  })
  if (error) return { ok: false, error: describe(error) }
  return { ok: true, data: data as PushOutcome }
}

export async function setScoringState(
  gameId: string,
  state: {
    roster: ScoringRosterEntry[]
    current_period: number
    period_format: string
    enabled_stat_types: string[]
    home_away?: string | null
    opponent_name?: string | null
  },
): Promise<Result<true>> {
  if (!supabase) return offline()
  const { error } = await supabase.rpc('set_scoring_state', { p_game_id: gameId, p_state: state })
  if (error) return { ok: false, error: describe(error) }
  return { ok: true, data: true }
}

// ---------------------------------------------------------------------------
// Realtime
// ---------------------------------------------------------------------------

/**
 * Watch one game's events and its scoring state.
 *
 * Realtime is a nice-to-have, not the source of truth: every subscriber also
 * polls, because a dropped websocket on a stadium wifi is common and a tally
 * that quietly stops updating looks identical to a game where nothing is
 * happening. `onStatus` reports what the connection is actually doing so the UI
 * can say so.
 */
export function watchGame(
  gameId: string,
  handlers: {
    onEvent?(row: ServerEventRow): void
    onState?(row: ScoringStateRow): void
    onClaim?(): void
    onStatus?(status: 'connected' | 'disconnected'): void
  },
): () => void {
  if (!supabase) return () => {}
  const channel = supabase
    .channel(`game:${gameId}`)
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'stat_events', filter: `game_id=eq.${gameId}` },
      (payload) => handlers.onEvent?.(payload.new as ServerEventRow),
    )
    .on(
      'postgres_changes',
      { event: '*', schema: 'public', table: 'game_scoring_state', filter: `game_id=eq.${gameId}` },
      (payload) => handlers.onState?.(payload.new as ScoringStateRow),
    )
    .on('postgres_changes', { event: '*', schema: 'public', table: 'game_claims', filter: `game_id=eq.${gameId}` }, () =>
      handlers.onClaim?.(),
    )
    .subscribe((status) => {
      handlers.onStatus?.(status === 'SUBSCRIBED' ? 'connected' : 'disconnected')
    })

  return () => {
    void supabase?.removeChannel(channel)
  }
}

/** Watch for any game being claimed or released, so a viewer notices tip-off. */
export function watchClaims(onChange: () => void): () => void {
  if (!supabase) return () => {}
  const channel = supabase
    .channel('claims')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'game_claims' }, () => onChange())
    .subscribe()
  return () => {
    void supabase?.removeChannel(channel)
  }
}
